// Основной режим — через воркер, минимальный fallback-скелет без игрового цикла
import type { WorkerInMessage, WorkerOutMessage } from '@/types/workerMessages';
import { getAudioEngine } from '@/audio/engine';
import { useGameStore } from '@/store/game';

interface Controls {
  pause: () => void;
  resume: () => void;
  isPaused: () => boolean;
  start?: () => void;
  restart?: () => void;
}
let currentControls: Controls | null = null;
export function pauseGame(): void {
  try {
    currentControls?.pause();
  } catch {
    // Игнорируем ошибки обработчика паузы
  }
}
export function resumeGame(): void {
  try {
    currentControls?.resume();
  } catch {
    // Игнорируем ошибки обработчика resume
  }
}
export function isGamePaused(): boolean {
  try {
    return currentControls?.isPaused() ?? false;
  } catch {
    return false;
  }
}
export function startGame(): void {
  try {
    currentControls?.start?.();
  } catch {
    // Игнорируем ошибки обработчика старта
  }
}
export function restartGame(): void {
  try {
    currentControls?.restart?.();
  } catch {
    // Игнорируем ошибки обработчика рестарта
  }
}

export function initGame(canvas: HTMLCanvasElement, onGameOver?: () => void, onGameStart?: () => void) {
  // --- Детект возможностей ---
  const gameStore = useGameStore();
  const hasOffscreen = typeof canvas.transferControlToOffscreen === 'function';
  const hasWorker = typeof Worker !== 'undefined';
  const canWorkerMode = hasOffscreen && hasWorker;
  const useWorker = canWorkerMode;

  if (useWorker) {
    canvas.style.touchAction = 'none';
    canvas.style.setProperty('-webkit-tap-highlight-color', 'transparent');
    canvas.style.setProperty('-webkit-user-select', 'none');

    // Звук (Web Audio: SFX + BGM)
    const audio = getAudioEngine();
    let audioUnlocked = false;
    const ensureAudioUnlocked = () => {
      if (audioUnlocked) return;
      audioUnlocked = true;
      // Разблокировка аудио строго в обработчике жеста, без цепочек then
      void audio.unlock().catch(() => {});
    };
    const playJumpSfx = () => audio.playSfx('jump');
    const playBlightSfx = () => audio.playSfx('blight');
    const playSkeletonSfx = () => audio.playSfx('skeleton');
    const playCoinSfx = () => audio.playSfx('coin');
    const playHeartSfx = () => audio.playSfx('heart');
    const playDeathSfx = () => audio.playSfx('death');

    // UI‑оверлеи и кнопочные изображения удалены: управление стартом/рестартом теперь во фронтенде

    // Фолбэк: если computedStyle ещё «прозрачный», берём дефолт
    const rawBg = getComputedStyle(canvas).backgroundColor;
    const isTransparentCss = !rawBg || rawBg === 'rgba(0, 0, 0, 0)' || rawBg === 'transparent';
    const skyColor =
      !isTransparentCss ? rawBg : '#e0f7fa';

    const offscreen = canvas.transferControlToOffscreen!();
    const worker = new Worker(new URL('./worker.entry.ts', import.meta.url), { type: 'module' }) as Worker & {
      postMessage: (msg: WorkerInMessage, transfer?: Transferable[]) => void;
      onmessage: ((this: Worker, ev: MessageEvent<WorkerOutMessage>) => void) | null;
    };

    const containerEl = canvas.parentElement;

    let lastCssW = -1;
    let lastCssH = -1;
    let debugHitboxes = false;
    const sendResize = (rect: DOMRectReadOnly): void => {
      const dpr = window.devicePixelRatio || 1;
      const w = Math.round(rect.width);
      const h = Math.round(rect.height);
      if (w === lastCssW && h === lastCssH) return;
      lastCssW = w; lastCssH = h;
      worker.postMessage({ type: 'resize', cssW: w, cssH: h, dpr });
    };

    // Дополнительно слушаем визуальный viewport (iOS меняет высоту/DPR при показе адресной строки)
    let vvTimer: number | null = null;
    const onVV = (): void => {
      if (vvTimer) {
        clearTimeout(vvTimer);
        vvTimer = null;
      }
      vvTimer = window.setTimeout(() => {
        const r = (containerEl ?? canvas).getBoundingClientRect();
        sendResize(r);
        vvTimer = null;
      }, 140);
    };
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', onVV);
      vv.addEventListener('scroll', onVV);
    }

    let roPending = false;
    const ro = new ResizeObserver((entries) => {
      if (roPending) return;
      roPending = true;
      const entry = entries[0];
      const rect = entry?.contentRect;
      requestAnimationFrame(() => {
        if (rect) sendResize(rect);
        else {
          const r = (containerEl ?? canvas).getBoundingClientRect();
          sendResize(r);
        }
        roPending = false;
      });
    });
    ro.observe(containerEl ?? canvas);

    // Инициализация: просим воркер дождаться первого resize (или таймаута)
    {
      const r = (containerEl ?? canvas).getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      debugHitboxes = (() => {
        try { return new URLSearchParams(location.search).get('hitboxes') === '1'; } catch { return false; }
      })();
      worker.postMessage(
        {
          type: 'init',
          canvas: offscreen,
          cssW: Math.round(r.width),
          cssH: Math.round(r.height),
          dpr,
          skyColor,
          expectResize: true,
          alpha: isTransparentCss,
          transparentBg: isTransparentCss,
          debugHitboxes,
        },
        [offscreen]
      );
    }

    // rAF-драйвер — стартуем только после 'ready' из воркера
    let rafId = 0;
    let started = false;
    let isReady = false;
    let startRequested = false;
    let paused = false;
    const shouldAutoStart = (() => {
      try {
        // Автозапуск только по URL-параметру ?autostart=1
        return new URLSearchParams(location.search).get('autostart') === '1';
      } catch {
        return false;
      }
    })();
    const drive = (t: number): void => {
      worker.postMessage({ type: 'frame', now: t });
      rafId = requestAnimationFrame(drive);
    };
    const applyPause = (nextPaused: boolean): void => {
      if (!started) {
        paused = nextPaused;
        return;
      }
      if (nextPaused === paused) return;
      paused = nextPaused;
      try {
        if (paused) {
          gameStore.pause();
        } else {
          gameStore.resume();
        }
      } catch {
        // Игнорируем сбои при обновлении стора (состояние игры остаётся консистентным)
      }
      try {
        worker.postMessage({ type: 'vis', hidden: paused });
      } catch {
        // Игнорируем сообщение, если воркер уже завершён
      }
      if (paused) {
        try {
          cancelAnimationFrame(rafId);
        } catch {
          // Игнорируем ошибку отмены rAF
        }
        try {
          audio.handleVisibility(true);
        } catch {
          // Игнорируем ошибку при передаче видимости в аудио‑движок
        }
      } else {
        try {
          rafId = requestAnimationFrame(drive);
        } catch {
          // Игнорируем ошибку запуска rAF
        }
        try {
          audio.handleVisibility(false);
        } catch {
          // Игнорируем ошибку при передаче видимости в аудио‑движок
        }
      }
    };
    let isGameOver = false;
    const start = (): void => {
      if (started) return;
      started = true;
      // при старте учитываем текущее состояние паузы/видимости
      try {
        worker.postMessage({ type: 'vis', hidden: document.hidden || paused });
      } catch {
        // Игнорируем ошибку первой синхронизации видимости
      }
      try {
        if (!paused) {
          gameStore.start();
        }
      } catch {
        // Игнорируем ошибку старта стора
      }
      if (!paused) {
        rafId = requestAnimationFrame(drive);
      }
    };
    const onVis = (): void => {
      if (!started) return;
      try {
        worker.postMessage({ type: 'vis', hidden: document.hidden });
      } catch {
        // Игнорируем ошибку, если воркер уже завершён
      }
      if (document.hidden) {
        try {
          cancelAnimationFrame(rafId);
        } catch {
          // Игнорируем ошибку отмены rAF
        }
        try {
          audio.handleVisibility(true);
        } catch {
          // Игнорируем ошибку при передаче видимости в аудио‑движок
        }
      } else if (!paused) {
        try {
          rafId = requestAnimationFrame(drive);
        } catch {
          // Игнорируем ошибку запуска rAF
        }
        try {
          audio.handleVisibility(false);
        } catch {
          // Игнорируем ошибку при передаче видимости в аудио‑движок
        }
      }
    };
    document.addEventListener('visibilitychange', onVis);

    worker.onmessage = (ev: MessageEvent<WorkerOutMessage>): void => {
      const data = ev.data || ({} as WorkerOutMessage);
      if (data.type === 'ui') {
        const coin =
          'coinCount' in data && typeof data.coinCount === 'number' ? data.coinCount : gameStore.coinCount;
        const life =
          'lifeCount' in data && typeof data.lifeCount === 'number' ? data.lifeCount : gameStore.lifeCount;
        try {
          gameStore.setCounts(coin, life);
        } catch {
          // Игнорируем ошибку обновления стора
        }
      } else if (data.type === 'ready') {
        isReady = true;
        try {
          gameStore.setPhase('ready');
        } catch {
          // Игнорируем ошибку обновления стора
        }
        if (startRequested && !started) start();
			} else if (data.type === 'sfx') {
        if (data.s === 'jump') {
          playJumpSfx();
        } else if (data.s === 'blight') {
          playBlightSfx();
        } else if (data.s === 'skeleton') {
          playSkeletonSfx();
        } else if (data.s === 'coin') {
          playCoinSfx();
        } else if (data.s === 'heart') {
          playHeartSfx();
        }
      } else if (data.type === 'death') {
        // Останавливаем музыку/движение сразу при смерти (но rAF сцены оставляем для анимации смерти)
        try {
          if (typeof onGameOver === 'function') onGameOver();
        } catch {
          // Игнорируем ошибку пользовательского колбэка
        }
        try {
          audio.stopBgm();
        } catch {
          // Игнорируем ошибку остановки фоновой музыки
        }
        playDeathSfx();
        try {
          gameStore.markDeath();
        } catch {
          // Игнорируем ошибку обновления стора
        }
      } else if (data.type === 'gameover') {
        if (isGameOver) return;
        isGameOver = true;
        try {
          cancelAnimationFrame(rafId);
        } catch {
          // Игнорируем ошибку отмены rAF
        }
        try {
          audio.stopBgm();
        } catch {
          // Игнорируем ошибку остановки фоновой музыки
        }
        paused = false;
        try {
          if (typeof onGameOver === 'function') onGameOver();
        } catch {
          // Игнорируем ошибку пользовательского колбэка
        }
        try {
          gameStore.markGameOver();
        } catch {
          // Игнорируем ошибку обновления стора
        }
      }
    };

    // Ввод → воркер
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.code === 'Space') {
        if (paused) { e.preventDefault(); return; }
        if (started) {
          worker.postMessage({ type: 'input', space: true });
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    const onTap = (): void => {
      if (paused) return;
      ensureAudioUnlocked();
      requestAnimationFrame(() => worker.postMessage({ type: 'input', tap: true }));
    };
    const onClickCapture = (e: MouseEvent): void => {
      e.preventDefault();
    };
    canvas.addEventListener('pointerdown', onTap, { passive: true });
    canvas.addEventListener('click', onClickCapture, { passive: false, capture: true });

    // Автозапуск после рестарта (без оверлеев)
    if (shouldAutoStart) {
      startRequested = true;
      ensureAudioUnlocked();
      void audio.startBgm();
      if (typeof onGameStart === 'function') {
        try {
          onGameStart();
        } catch {
          // Игнорируем ошибку пользовательского колбэка
        }
      }
      try {
        gameStore.start();
      } catch {
        // Игнорируем ошибку обновления стора
      }
      if (isReady && !started) start();
    }

    // Экспортируем управление паузой наружу
    currentControls = {
      pause: (): void => applyPause(true),
      resume: (): void => applyPause(false),
      isPaused: (): boolean => paused,
      start: (): void => {
        if (started) return;
        startRequested = true;
        ensureAudioUnlocked();
        void audio.startBgm();
        if (typeof onGameStart === 'function') {
          try {
            onGameStart();
          } catch {
            // Игнорируем ошибку пользовательского колбэка
          }
        }
        if (isReady && !started) start();
      },
      restart: (): void => {
        // Мягкий рестарт без перезагрузки страницы:
        // 1) сбросить игровое состояние в сторе,
        // 2) попросить воркер сбросить рантайм,
        // 3) перезапустить BGM и rAF, как при первом старте.
        isGameOver = false;
        paused = false;
        startRequested = true;
        started = false;

        try {
          gameStore.reset();
        } catch {
          // Игнорируем ошибку сброса стора — воркер всё равно будет сброшен
        }

        try {
          worker.postMessage({ type: 'reset' });
        } catch {
          // Игнорируем ошибку отправки сообщения reset
        }

        ensureAudioUnlocked();
        void audio.startBgm();

        if (typeof onGameStart === 'function') {
          try {
            onGameStart();
          } catch {
            // Игнорируем ошибку пользовательского колбэка
          }
        }

        if (isReady && !started) start();
      },
    };

    // не запускаем локальный рендер — возвращаем функцию очистки
    return (): void => {
      try {
        document.removeEventListener('visibilitychange', onVis);
        window.removeEventListener('keydown', onKeyDown);
        canvas.removeEventListener('pointerdown', onTap, { passive: true });
        canvas.removeEventListener('click', onClickCapture, { capture: true });
        try {
          vv?.removeEventListener('resize', onVV);
        } catch {
          // Игнорируем ошибку удаления обработчика resize visualViewport
        }
        try {
          vv?.removeEventListener('scroll', onVV);
        } catch {
          // Игнорируем ошибку удаления обработчика scroll visualViewport
        }
        ro.disconnect();
        if (started) {
          cancelAnimationFrame(rafId);
        }
        try {
          audio.stopBgm();
        } catch {
          // Игнорируем ошибку остановки BGM во время очистки
        }
      } finally {
        currentControls = null;
        try {
          worker.terminate();
        } catch {
          // Игнорируем ошибку при завершении воркера
        }
      }
    };
  }

  // ------------------ Минимальный fallback-скелет ------------------
  const msg = document.createElement('div');
  msg.setAttribute('role', 'status');
  msg.textContent = 'Упрощённый режим: ускоренный рендер недоступен в этом окружении.';
  canvas.insertAdjacentElement('afterend', msg);
  return (): void => {
    try {
      msg.remove();
    } catch {
      // Игнорируем ошибки удаления DOM-элемента в fallback‑режиме
    }
  };
}
